import { styled } from '@linaria/react';
import { Collapse, Group, Stack } from '@mantine/core';
import html2canvas from 'html2canvas';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { Goal, Outcome } from '../../../../api/strategy-client/strategy-client.type';
import { icons } from '../../../../assets/icons/icons';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { baseWhite, inkDark, secondaryBase, skyDark, skyLight } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { getRiskCategory } from '../../../adherence/risk-analysis/risk-analysis.helpers';
import { useFlora } from '../../../flora/flora.context';
import { FloraSupportedObjectTypes } from '../../../flora/flora.type';
import { GeneralViewNames } from '../../../side-bar/side-bar.type';
import { InitiativesTable } from './initiatives-table';
import { OutcomeRiskSection } from './outcome-risk-section';
import { OverviewInfoCards } from './overview-info-cards';
import { ProgressOverTimeChart } from './progress-over-time-chart';
import { TeamFocusChart } from './team-focus-chart';
import { ThirtyDaySummarySection } from './thirty-day-summary-section';
import { WorkCommittedCompletedChart } from './work-committed-completed-chart';

type Props = {
  outcome: Outcome;
  goals: Goal[];
  onBack: () => void;
};

export const OutcomeOverviewDetailView = ({ outcome, goals, onBack }: Props) => {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const useAI = useGlobalStore((state) => state.useAI);
  const { askQuestion, setContextualInfo } = useFlora();
  const outcomeCompletionPercent = Math.round(outcome.percent_progress || 0);
  const contentRef = useRef<HTMLDivElement>(null);

  const [isExporting, setIsExporting] = useState(false);

  // Individual section collapsed states (default expanded)
  const [kpisExpanded, setKpisExpanded] = useState(true);
  const [summaryRiskExpanded, setSummaryRiskExpanded] = useState(true);
  const [chartsExpanded, setChartsExpanded] = useState(true);
  const [workChartsExpanded, setWorkChartsExpanded] = useState(true);
  const [initiativesExpanded, setInitiativesExpanded] = useState(true);

  const handleExport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExporting(true);

    // Store current expansion state
    const wasKpisExpanded = kpisExpanded;
    const wasSummaryRiskExpanded = summaryRiskExpanded;
    const wasChartsExpanded = chartsExpanded;
    const wasWorkChartsExpanded = workChartsExpanded;
    const wasInitiativesExpanded = initiativesExpanded;

    // Temporarily expand everything
    setKpisExpanded(true);
    setSummaryRiskExpanded(true);
    setChartsExpanded(true);
    setWorkChartsExpanded(true);
    setInitiativesExpanded(true);

    // Wait for animations to complete
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      if (contentRef.current) {
        const canvas = await html2canvas(contentRef.current, {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true,
        });

        const link = document.createElement('a');
        link.download = `${outcome.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_overview.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    } catch (error) {
      console.error('Error exporting:', error);
    } finally {
      // Restore previous expansion states
      setKpisExpanded(wasKpisExpanded);
      setSummaryRiskExpanded(wasSummaryRiskExpanded);
      setChartsExpanded(wasChartsExpanded);
      setWorkChartsExpanded(wasWorkChartsExpanded);
      setInitiativesExpanded(wasInitiativesExpanded);
      setIsExporting(false);
    }
  };

  const handleFloraAnalysis = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Set contextual info for Flora
    setContextualInfo({
      currentModule: GeneralViewNames.Strategy,
      currentObjects: [{ type: FloraSupportedObjectTypes.Objective, id: outcome.id, name: outcome.name }],
      startDate: outcome.start_date ?? null,
      endDate: outcome.end_date ?? null,
    });

    // Open Flora with a pre-filled question about analyzing the outcome
    askQuestion(
      `Analyze this outcome's performance. Include insights on progress, risk factors, team focus, and recommendations for improvement.`,
    );
  };

  // Show first goal name + " + X" if more than 1, or just name, or undefined
  const goalNamesDisplay =
    goals.length > 0 ? (goals.length === 1 ? goals[0].name : `${goals[0].name} +${goals.length - 1}`) : undefined;

  const riskScore = outcome.flora_annotations?.risk_score;
  const riskCategory = riskScore ? getRiskCategory(riskScore) : null;

  return (
    <PageContainer>
      {/* Header with Back Button, Title, and Action Buttons */}
      <HeaderSection>
        <LeftSection>
          <Button variant="secondary" size="sm" radius="xl" onClick={onBack}>
            <Icon name="arrow_back" color={baseWhite} size={20} style={{ cursor: 'pointer' }} />
          </Button>
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            {goalNamesDisplay && (
              <Tooltip
                variant="black"
                label={
                  <Stack gap={2}>
                    <Text size="medium" weight="medium">
                      {i18n.t('common.strategic_goals')}
                    </Text>
                    {goals.map((goal) => (
                      <Text key={goal.id}>{goal.name}</Text>
                    ))}
                  </Stack>
                }
                zIndex={1000}
              >
                <Text weight="medium" color={skyDark} size="small">
                  {goals.length > 0 ? goalNamesDisplay : 'No associated goals'}
                </Text>
              </Tooltip>
            )}
            <OutcomeName
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/application/strategy/outcomes/${outcome.id}`);
              }}
            >
              {outcome.name}
            </OutcomeName>
          </Stack>
        </LeftSection>
        <RightSection>
          {riskScore && riskCategory && (
            <RiskBadge $color={riskCategory.color}>
              <Text size="medium" weight="bold" style={{ color: '#000' }}>
                {riskScore.toFixed(1)}
              </Text>
            </RiskBadge>
          )}
          {useAI && (
            <Tooltip variant="black" label="Analyze with Flora" zIndex={1000}>
              <FloraButton onClick={handleFloraAnalysis}>
                <img src={icons.iconFlora} alt="Flora AI" style={{ width: 20, height: 20 }} />
              </FloraButton>
            </Tooltip>
          )}
          <Tooltip variant="black" label="Export as image" zIndex={1000}>
            <ExportButton onClick={handleExport} disabled={isExporting}>
              <Icon
                name={isExporting ? 'hourglass_empty' : 'download'}
                style={{ cursor: isExporting ? 'not-allowed' : 'pointer' }}
                size={20}
                color={skyDark}
              />
            </ExportButton>
          </Tooltip>
        </RightSection>
      </HeaderSection>

      {/* Content Sections */}
      <ContentContainer ref={contentRef}>
        <Stack gap={24}>
          {/* Info Cards Section */}
          <CollapsibleSection>
            <SectionHeader onClick={() => setKpisExpanded(!kpisExpanded)}>
              <Text weight="medium" size="medium" color={inkDark}>
                {i18n.t('strategy.overview.title')}
              </Text>
              <Icon name={kpisExpanded ? 'expand_less' : 'expand_more'} size={20} color={skyDark} />
            </SectionHeader>
            <Collapse expanded={kpisExpanded}>
              <Stack gap={12}>
                <OverviewInfoCards
                  metrics={outcome.overview_metrics}
                  overallProgress={outcomeCompletionPercent}
                  entityType="outcome"
                />
              </Stack>
            </Collapse>
          </CollapsibleSection>

          {/* 30-Day Summary + Risk Section */}
          <CollapsibleSection>
            <SectionHeader onClick={() => setSummaryRiskExpanded(!summaryRiskExpanded)}>
              <Text weight="medium" size="medium" color={inkDark}>
                Summary & Risk Analysis
              </Text>
              <Icon name={summaryRiskExpanded ? 'expand_less' : 'expand_more'} size={20} color={skyDark} />
            </SectionHeader>
            <Collapse expanded={summaryRiskExpanded}>
              <Stack gap={12}>
                <SplitRow>
                  <ThirtyDaySummarySection floraAnnotations={outcome.flora_annotations} />
                  <OutcomeRiskSection floraAnnotations={outcome.flora_annotations} useAI={useAI} entityType="outcome" />
                </SplitRow>
              </Stack>
            </Collapse>
          </CollapsibleSection>

          {/* Charts Section */}
          <CollapsibleSection>
            <SectionHeader onClick={() => setChartsExpanded(!chartsExpanded)}>
              <Text weight="medium" size="medium" color={inkDark}>
                Progress & Team Focus
              </Text>
              <Icon name={chartsExpanded ? 'expand_less' : 'expand_more'} size={20} color={skyDark} />
            </SectionHeader>
            <Collapse expanded={chartsExpanded}>
              <Stack gap={12}>
                <SplitRow>
                  <ProgressOverTimeChart outcomeId={outcome.id} />
                  <TeamFocusChart outcomeId={outcome.id} />
                </SplitRow>
              </Stack>
            </Collapse>
          </CollapsibleSection>

          {/* Work by Project Charts Section */}
          <CollapsibleSection>
            <SectionHeader onClick={() => setWorkChartsExpanded(!workChartsExpanded)}>
              <Text weight="medium" size="medium" color={inkDark}>
                Work Committed & Completed by Project
              </Text>
              <Icon name={workChartsExpanded ? 'expand_less' : 'expand_more'} size={20} color={skyDark} />
            </SectionHeader>
            <Collapse expanded={workChartsExpanded}>
              <Stack gap={12}>
                <WorkCommittedCompletedChart outcomeId={outcome.id} />
              </Stack>
            </Collapse>
          </CollapsibleSection>

          {/* Initiatives Table Section */}
          <CollapsibleSection>
            <SectionHeader onClick={() => setInitiativesExpanded(!initiativesExpanded)}>
              <Text weight="medium" size="medium" color={inkDark}>
                {i18n.t('strategy.overview.sections.initiatives_table')}
              </Text>
              <Icon name={initiativesExpanded ? 'expand_less' : 'expand_more'} size={20} color={skyDark} />
            </SectionHeader>
            <Collapse expanded={initiativesExpanded}>
              <Stack gap={12}>
                <InitiativesTable initiatives={outcome.initiatives || []} />
              </Stack>
            </Collapse>
          </CollapsibleSection>
        </Stack>
      </ContentContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 16px;
  margin: -16px -24px;
  border-radius: 12px;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  background-color: transparent;
  border-radius: 12px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const RightSection = styled(Group)`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const OutcomeName = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 24px;
  font-weight: 600;
  word-break: break-word;
  color: #000000;
  line-height: 1.2;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${secondaryBase};
  }
`;

const RiskBadge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 16px;
  background-color: ${(props) => `${props.$color}20`};
  border: 1px solid ${(props) => props.$color};
  flex-shrink: 0;
  min-width: 45px;
`;

const FloraButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: transparent;
  border: 1px solid ${skyLight};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border-color: ${secondaryBase};
  }
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: transparent;
  border: 1px solid ${skyLight};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background-color: rgba(0, 0, 0, 0.05);
    border-color: ${secondaryBase};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CollapsibleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: transparent;
  border-radius: 12px;
  padding: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  cursor: pointer;
  user-select: none;
  border-radius: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
`;

const SplitRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;
